import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronDown, Heart, Sparkles, Calendar, Briefcase, Award, Users, Aperture } from 'lucide-react';
import WhatWeDo from '@/components/WhatWeDo';
import Testimonials from '@/components/Testimonials';

const HERO_IMAGE = '/images/Home/Hero.png';

/* Polaroid frames — dark-themed, scattered, floating in hero */
const POLAROID_FRAMES = [
  { src: '/images/Polaroid/Polaroid_Frames_1.jpeg',
    alt: 'Wedding', rotate: -8,
    pos: 'left-[1%] sm:left-[3%] bottom-[8%] sm:bottom-[12%]',
    w: 'w-[100px] sm:w-[150px] lg:w-[190px]', h: 'h-[120px] sm:h-[180px] lg:h-[230px]',
    z: 'z-[11]', delay: 1.2, float: 'float-1' },
  { src: '/images/Polaroid/Polaroid_Frames_2.jpeg',
    alt: 'Couple', rotate: 5,
    pos: 'left-[10%] sm:left-[14%] bottom-[-2%] sm:bottom-[0%]',
    w: 'w-[90px] sm:w-[135px] lg:w-[170px]', h: 'h-[115px] sm:h-[170px] lg:h-[215px]',
    z: 'z-[12]', delay: 1.4, float: 'float-4' },
  { src: '/images/Polaroid/Polaroid_Frames_3.png',
    alt: 'Ceremony', rotate: -2,
    pos: 'left-[26%] sm:left-[30%] bottom-[-6%] sm:bottom-[-2%]',
    w: 'w-[110px] sm:w-[160px] lg:w-[210px]', h: 'h-[140px] sm:h-[200px] lg:h-[260px]',
    z: 'z-[13]', delay: 1.0, float: 'float-2' },
  { src: '/images/Polaroid/Polaroid_Frames_4.jpeg',
    alt: 'Fashion', rotate: 6,
    pos: 'right-[26%] sm:right-[28%] bottom-[-4%] sm:bottom-[0%]',
    w: 'w-[100px] sm:w-[145px] lg:w-[185px]', h: 'h-[130px] sm:h-[185px] lg:h-[235px]',
    z: 'z-[12]', delay: 1.15, float: 'float-5' },
  { src: '/images/Polaroid/Polaroid_Frames_5.jpeg',
    alt: 'Event', rotate: -5,
    pos: 'right-[10%] sm:right-[13%] bottom-[2%] sm:bottom-[6%]',
    w: 'w-[95px] sm:w-[140px] lg:w-[180px]', h: 'h-[125px] sm:h-[180px] lg:h-[225px]',
    z: 'z-[11]', delay: 1.35, float: 'float-3' },
  { src: '/images/Polaroid/Polaroid_Frames_6.jpeg',
    alt: 'Product', rotate: 8,
    pos: 'right-[0%] sm:right-[1%] bottom-[14%] sm:bottom-[16%]',
    w: 'w-[85px] sm:w-[125px] lg:w-[160px]', h: 'h-[105px] sm:h-[155px] lg:h-[200px]',
    z: 'z-[10]', delay: 1.5, float: 'float-6' },
];

const SERVICE_CARDS = [
  { title: 'Wedding', desc: 'Timeless love stories captured with cinematic elegance.', icon: Heart, path: '/wedding',
    image: '/images/Polaroid/Polaroid_Frames_1.jpeg' },
  { title: 'Fashion', desc: 'Editorial elegance meets bold creativity.', icon: Sparkles, path: '/fashion',
    image: '/images/Polaroid/Polaroid_Frames_4.jpeg'},
  { title: 'Events', desc: 'Corporate galas and cultural celebrations.', icon: Calendar, path: '/events',
    image: '/images/Polaroid/Polaroid_Frames_5.jpeg' },
  { title: 'Commercial', desc: 'Product shoots and brand identity campaigns.', icon: Briefcase, path: '/commercial',
    image: '/images/Polaroid/Polaroid_Frames_6.jpeg'},
];

const STATS = [
  { icon: Aperture, value: '50+', label: 'Projects Delivered' },
  { icon: Users, value: '35+', label: 'Happy Clients' },
  // { icon: Award, value: '10+', label: 'Years of Excellence' },
];

export default function HomePage() {
  const scrollDown = (e) => {
    e.preventDefault();
    document.getElementById('services-overview')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* ===== HERO SECTION ===== */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-visible">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMAGE} alt="Cinematic photography" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-visyra-bg" />
          <div className="absolute inset-0 bg-black/30" />
        </div>

        {/* Hero Text */}
        <div className="relative z-20 max-w-4xl mx-auto px-6 text-center">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}
            data-testid="hero-tagline" className="font-body text-xs sm:text-sm uppercase tracking-[0.3em] text-gold mb-6 sm:mb-8">
            Capturing Moments 
          </motion.p>

          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.5 }}
            data-testid="hero-heading" className="font-heading text-4xl sm:text-5xl lg:text-7xl tracking-tight text-visyra-text leading-tight mb-2">
            Artistic Storytelling
          </motion.h1>

          <motion.h2 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.7 }}
            data-testid="hero-subheading" className="font-heading italic text-4xl sm:text-5xl lg:text-7xl tracking-tight text-gold leading-tight mb-8 sm:mb-10">
            Celebrating YOU
          </motion.h2>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.9 }}
            className="font-body text-sm sm:text-base text-visyra-muted max-w-lg mx-auto mb-10 sm:mb-12 leading-relaxed">
            We are an premium photography and cinematography brand, known for our artistic, professional, and customer-centric approach.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.1 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
            <Link to="/services" data-testid="hero-view-services-btn"
              className="btn-shimmer font-body text-sm font-medium uppercase tracking-widest bg-gold text-visyra-bg px-8 py-3.5 hover:bg-gold-light transition-all duration-400 w-full sm:w-auto text-center">
              View Services
            </Link>
            <Link to="/contact" data-testid="hero-get-in-touch-btn"
              className="btn-shimmer font-body text-sm font-medium uppercase tracking-widest text-visyra-text border border-white/30 px-8 py-3.5 hover:border-gold hover:text-gold transition-all duration-400 w-full sm:w-auto text-center">
              Get in Touch
            </Link>
          </motion.div>
        </div>

        {/* Polaroid frames — dark themed, scattered, floating */}
        <div className="absolute inset-0 pointer-events-none hidden sm:block" style={{ zIndex: 15 }}>
          {POLAROID_FRAMES.map((frame) => (
            <div
              key={frame.alt}
              className={`absolute ${frame.pos} ${frame.z} pointer-events-auto ${frame.float}`}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.3, rotate: frame.rotate }}
                animate={{ opacity: 1, scale: 1, rotate: frame.rotate }}
                transition={{ duration: 0.8, delay: frame.delay, ease: [0.34, 1.56, 0.64, 1] }}
                whileHover={{ y: -18, scale: 1.1, rotate: 0, transition: { duration: 0.3, ease: 'easeOut' } }}
                className="cursor-pointer"
              >
                <div className={`${frame.w} p-[6px] sm:p-2 bg-visyra-bg/80 backdrop-blur-sm border border-white/8 shadow-2xl shadow-black/60 transition-shadow duration-300 hover:shadow-[0_20px_60px_rgba(212,175,55,0.15)]`}>
                  <div className={`${frame.h} overflow-hidden`}>
                    <img src={frame.src} alt={frame.alt} className="w-full h-full object-cover opacity-85" loading="eager" />
                  </div>
                  <div className="h-4 sm:h-6" />
                </div>
              </motion.div>
            </div>
          ))}
        </div>

        <motion.button onClick={scrollDown} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.8 }}
          data-testid="scroll-indicator" className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 text-visyra-muted hover:text-gold transition-colors duration-300" aria-label="Scroll down">
          <ChevronDown size={28} className="animate-bounce-chevron" />
        </motion.button>
      </section>

      {/* ===== SERVICES OVERVIEW ===== */}
      <section id="services-overview" data-testid="services-overview" className="py-24 sm:py-32 bg-visyra-bg">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="text-center mb-16 sm:mb-20">
            <p className="font-body text-xs uppercase tracking-[0.3em] text-gold mb-4">What We Offer</p>
            <h2 data-testid="services-heading" className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-visyra-text mb-5">Our Services</h2>
            <div className="section-divider mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICE_CARDS.map((service, i) => {
              const Icon = service.icon;
              return (
                <motion.div key={service.title} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }}>
                  <Link to={service.path} data-testid={`service-card-${service.title.toLowerCase()}`}
                    className="group block bg-visyra-card border border-white/5 hover:border-gold/30 transition-all duration-500 overflow-hidden">
                    <div className="h-48 overflow-hidden">
                      <img src={service.image} alt={service.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-2">
                        <Icon size={18} className="text-gold" strokeWidth={1.5} />
                        <h3 className="font-heading text-xl text-visyra-text tracking-tight">{service.title}</h3>
                      </div>
                      <p className="font-body text-sm text-visyra-muted">{service.desc}</p>
                    </div>
                    <div className="h-[2px] w-0 bg-gold group-hover:w-full transition-all duration-700" />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== ABOUT PREVIEW ===== */}
      <section data-testid="about-preview" className="py-24 sm:py-32 bg-visyra-paper">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }}>
              <div className="relative">
                <img src="/images/Home/About_me.jpeg"
                  alt="Photographer at work" className="w-full h-[400px] sm:h-[500px] object-cover" loading="lazy" />
                <div className="absolute -bottom-3 -right-3 w-full h-full border border-gold/20 -z-10" />
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.2 }}>
              <p className="font-body text-xs uppercase tracking-[0.3em] text-gold mb-4">Our Story</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-visyra-text mb-6 leading-tight">
                Where Art Meets <span className="italic text-gold">Emotion</span>
              </h2>
              <div className="section-divider mb-8" />
              <p className="font-body text-sm sm:text-base text-visyra-muted leading-relaxed mb-6">
                At Visyra, we believe every frame should tell a story. Founded with a passion for cinematic visual storytelling, we have spent years crafting images that transcend the ordinary.
              </p>
              <div className="grid grid-cols-3 gap-6 mb-8">
                {STATS.map((stat) => {
                  const StatIcon = stat.icon;
                  return (
                    <div key={stat.label} className="text-center lg:text-left">
                      <StatIcon size={20} className="text-gold mb-2 mx-auto lg:mx-0" strokeWidth={1.5} />
                      <p className="font-heading text-2xl text-visyra-text tracking-tight">{stat.value}</p>
                      <p className="font-body text-xs text-visyra-dim uppercase tracking-wider mt-1">{stat.label}</p>
                    </div>
                  );
                })}
              </div>
              <Link to="/about" className="inline-block font-body text-sm uppercase tracking-widest text-gold border-b border-gold pb-1 hover:text-gold-light hover:border-gold-light transition-colors duration-300">
                Learn More About Us
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== WHAT WE DO ===== */}
      <WhatWeDo />

      {/* ===== TESTIMONIALS ===== */}
      <Testimonials />

      {/* ===== CONTACT CTA ===== */}
      <section data-testid="contact-cta" className="py-24 sm:py-32 bg-visyra-bg">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <p className="font-body text-xs uppercase tracking-[0.3em] text-gold mb-4">Let's Create Together</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-visyra-text mb-6">
              Ready to Tell <span className="italic text-gold">Your Story?</span>
            </h2>
            <p className="font-body text-sm sm:text-base text-visyra-muted leading-relaxed mb-10 max-w-lg mx-auto">
              Whether it's a dream wedding, a fashion campaign, or a corporate event — we'd love to bring your vision to life.
            </p>
            <Link to="/contact" data-testid="cta-enquire-btn"
              className="btn-shimmer inline-block font-body text-sm font-medium uppercase tracking-widest bg-gold text-visyra-bg px-10 py-4 hover:bg-gold-light transition-all duration-400">
              Enquire Now
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
